/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";

import { useHistory, useParams } from "react-router-dom";

import { DeveloperCompanyModel } from "../../../../../domain/models/developer-company.model";

import RecurrencyForm from "../../../../../presentation/pages/developer/recurrency-mutation/form";

import toastUtils from "../../../../../utils/ToastUtils";
import { getErrorMessage } from "../../../../../presentation/helpers";

import { makeRemoteDeveloperCompanies } from "../../../usecases/developer-companies/remote-developer-companies-factory";
import { makeRemoteFiscalNote, makeRemoteFiscalNoteRecorrency, makeRemoteRecorrencyFiscalNote } from "../../../usecases/fiscal-note/remote-fiscal-note-factory";
import { NFSeTypeEnum } from "../../../../../presentation/enums/nfs-e-type.enum";
import { FiscalNoteModel, RecurrenceMonthlyStatus } from "../../../../../domain/models/fiscal-note";

const MakeRecurrencyMutation: React.FC = () => {
  const [developerCompaniesList, setDeveloperCompaniesList] = React.useState<
    DeveloperCompanyModel[]
  >([]);

  const { id } = useParams<{ id?: string }>();
  const isToCreate = !id;

  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [fiscalNote, setFiscalNote] = React.useState<FiscalNoteModel | undefined>();
  const [monthlyStatus, setMonthlyStatus] = React.useState<RecurrenceMonthlyStatus | null>(null);

  const history = useHistory();

  React.useEffect(() => {
    (async () => {
      const econ = await makeRemoteDeveloperCompanies().getByDeveloper();

      setDeveloperCompaniesList(econ);
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      if (isToCreate) return;
      const idParam = Number(id);
      if (!idParam) {
        toastUtils.showMessage({
          type: "warning",
          message: "Nota recorrente não encontrada!",
        });
        setTimeout(() => {
          history.push("/recurrency");
        }, 2000);
        return;
      };

      const [econ, status] = await Promise.all([
        makeRemoteFiscalNote().loadById(idParam),
        makeRemoteRecorrencyFiscalNote().getMonthlyStatus(idParam).catch(() => null),
      ]);

      setFiscalNote(econ);
      setMonthlyStatus(status);
    })();
  }, []);

  const handleCreate = async (
    values: any,
    setShowConfirm: any,
  ): Promise<void> => {
    setSubmitting(true);
    try {
      if (!values?.taker?.id) {
        toastUtils.showMessage({
          type: "warning",
          message: "Por favor, selecione um tomador!",
        });
        setSubmitting(false);
        return;
      }

      if (!values.dayOfRecurrence) {
        toastUtils.showMessage({
          type: "warning",
          message: "Por favor, selecione uma data para a recorrência!",
        });
        setSubmitting(false);
        return;
      }

      const toSend = {
        developerCompany: {
          id: values?.company,
        },
        observation: values?.description,
        serviceList: [
          {
            conditionedDiscount: Number(
              values?.conditionedDiscount
                ?.replaceAll(".", "")
                .replaceAll(",", "."),
            ),
            cnae: {
              id: values?.cnae?.id,
            },
            nationalService: {
              id: values?.nationalService,
            },
            nbs: {
              id: values?.nbs,
            },
            issAliquot: Number(
              values.simplesNacional
                ? 0
                : values?.issAliquot?.replaceAll(".", "").replaceAll(",", "."),
            ),
            issRetained: values?.iss === "true",
            otherDeductions: Number(
              values?.otherDeductions?.replaceAll(".", "").replaceAll(",", "."),
            ),
            serviceValue: Number(
              values?.serviceValue?.replaceAll(".", "").replaceAll(",", "."),
            ),
            unconditionalDiscount: Number(
              values?.unconditionalDiscount
                ?.replaceAll(".", "")
                .replaceAll(",", "."),
            ),
            // Dados de obra
            ...(values?.obraTipo ? {
              obraTipo: values.obraTipo,
              obraCodigo: values.obraTipo === "CODIGO" ? values.obraCodigo : undefined,
              obraInscricaoImobFisc: values.obraInscricaoImobFisc || undefined,
              obraAddress: values.obraTipo === "ENDERECO" && values.obraAddress ? {
                place: values.obraAddress.place,
                district: values.obraAddress.district,
                number: values.obraAddress.number,
                complement: values.obraAddress.complement,
                city: values.obraAddress.city,
                state: values.obraAddress.state,
                zipCode: values.obraAddress.zipCode?.replace(/\D/g, ""),
                tipoLogradouro: values.obraAddress.tipoLogradouro,
              } : undefined,
            } : {}),
          },
        ],
        taker: {
          id: values?.taker?.id,
        },
        competencia: values?.competencia,
        emissionDate: values?.emissionDate,
        hasRecurrence: true,
        hasNationalFiscal: values.nfseType === NFSeTypeEnum.NFS_E_NACIONAL,
      };

      Object.assign(toSend, {
        dayOfRecurrence: values.dayOfRecurrence,
      });

      await makeRemoteFiscalNote().create(toSend);
      setShowConfirm(true);
      setTimeout(() => {
        setShowConfirm(false);
        history.push("/recurrency");
      }, 6000);
      toastUtils.showMessage({
        type: "success",
        message: "Criado com Sucesso!",
      });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toastUtils.showMessage({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (
    values: any,
    setShowConfirm: any,
  ): Promise<void> => {

    const idParam = Number(id);
    if (!idParam) {
      toastUtils.showMessage({
        type: "warning",
        message: "Nota recorrente não encontrada!",
      });
      setTimeout(() => {
        history.push("/recurrency");
      }, 2000);
      return;
    };

    setSubmitting(true);
    try {
      if (!values?.taker?.id) {
        toastUtils.showMessage({
          type: "warning",
          message: "Por favor, selecione um tomador!",
        });
        setSubmitting(false);
        return;
      }

      if (!values.dayOfRecurrence) {
        toastUtils.showMessage({
          type: "warning",
          message: "Por favor, selecione uma data para a recorrência!",
        });
        setSubmitting(false);
        return;
      }

      const toSend = {
        developerCompany: {
          id: values?.company,
        },
        observation: values?.description,
        serviceList: [
          {
            conditionedDiscount: Number(
              values?.conditionedDiscount
                ?.replaceAll(".", "")
                .replaceAll(",", "."),
            ),
            cnae: {
              id: typeof values?.cnae === "object" ? values?.cnae?.id : values?.cnae,
            },
            nationalService: {
              id: typeof values?.nationalService === "object" ? values?.nationalService?.id : values?.nationalService,
            },
            nbs: {
              id: typeof values?.nbs === "object" ? values?.nbs?.id : values?.nbs,
            },
            issAliquot: Number(
              values.simplesNacional
                ? 0
                : values?.issAliquot?.replaceAll(".", "").replaceAll(",", "."),
            ),
            issRetained: values?.iss === "true",
            otherDeductions: Number(
              values?.otherDeductions?.replaceAll(".", "").replaceAll(",", "."),
            ),
            serviceValue: Number(
              values?.serviceValue?.replaceAll(".", "").replaceAll(",", "."),
            ),
            unconditionalDiscount: Number(
              values?.unconditionalDiscount
                ?.replaceAll(".", "")
                .replaceAll(",", "."),
            ),
            // Dados de obra
            ...(values?.obraTipo ? {
              obraTipo: values.obraTipo,
              obraCodigo: values.obraTipo === "CODIGO" ? values.obraCodigo : undefined,
              obraInscricaoImobFisc: values.obraInscricaoImobFisc || undefined,
              obraAddress: values.obraTipo === "ENDERECO" && values.obraAddress ? {
                place: values.obraAddress.place,
                district: values.obraAddress.district,
                number: values.obraAddress.number,
                complement: values.obraAddress.complement,
                city: values.obraAddress.city,
                state: values.obraAddress.state,
                zipCode: values.obraAddress.zipCode?.replace(/\D/g, ""),
                tipoLogradouro: values.obraAddress.tipoLogradouro,
              } : undefined,
            } : {}),
          },
        ],
        taker: {
          id: values?.taker?.id,
        },
        competencia: values?.competencia,
        emissionDate: values?.emissionDate,
        hasRecurrence: true,
        hasNationalFiscal: values.nfseType === NFSeTypeEnum.NFS_E_NACIONAL,
      };

      Object.assign(toSend, {
        dayOfRecurrence: values.dayOfRecurrence,
      });

      await makeRemoteFiscalNoteRecorrency().update(idParam, toSend);
      setShowConfirm(true);
      setTimeout(() => {
        setShowConfirm(false);
        history.push("/recurrency");
      }, 6000);
      toastUtils.showMessage({
        type: "success",
        message: "Atualizado com Sucesso!",
      });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toastUtils.showMessage({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (values: any, setShowConfirm: any): Promise<void> => {
    if (isToCreate) {
      return handleCreate(values, setShowConfirm);
    }

    return handleUpdate(values, setShowConfirm);
  };

  return (
    <RecurrencyForm
      fiscalNote={fiscalNote}
      handleSubmit={onSubmit}
      developerCompaniesList={developerCompaniesList}
      submitting={submitting}
      isToCreate={isToCreate}
      monthlyStatus={monthlyStatus}
    />
  );
};

export default MakeRecurrencyMutation;
